import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PhoneIcon,
  PlayIcon,
  StopIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DocumentTextIcon,
  XMarkIcon,
  MicrophoneIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  LinkIcon,
  BookmarkIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { api, API_BASE_URL } from '../services/api';
import { listFieldTemplates, createFieldTemplate, deleteFieldTemplate } from '../services/campaignApi';
import toast from 'react-hot-toast';
import { PhoneInput } from '../components/ui/PhoneInput';
import { callStatuses } from '../mocks/calls';
import { useAuth } from '../contexts/AuthContext';
import {
  DEMO_FIXTURE_PATH,
  buildWebCallPayload,
  demoEchoConfirmed,
  isUsableDemoFixture,
} from '../utils/webCallDemo';

// Web Call component with visual circle indicator for call state
function Calls() {
  const { t } = useTranslation();
  const { tenant } = useAuth() || {};
  const tenantKey = tenant?.tenant_id || tenant?.id || '';
  const [phoneCalls, setPhoneCalls] = useState([]);
  const [webCalls, setWebCalls] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [activeTab, setActiveTab] = useState('web');
  const [webCallActive, setWebCallActive] = useState(false);
  const [activeWebSession, setActiveWebSession] = useState(null);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false); // For UI indicator

  // Available from numbers
  const [fromNumbers, setFromNumbers] = useState([]);

  // Phone call form
  const [phoneCallForm, setPhoneCallForm] = useState({
    agent_id: '',
    phone_number: '',
    twilio_phone_number: '',
    handoff_phone_number: '',
    test_webhook_url: '',
    gender_switch: false,
  });

  // Web call form
  const [webCallForm, setWebCallForm] = useState({
    agent_id: '',
    test_webhook_url: '',
    gender_switch: false,
  });

  // "Use demo data" (web form only; never read by the phone form). The fixture
  // is server-owned; the browser only previews it. OFF by default.
  const [webDemoMode, setWebDemoMode] = useState(false);
  const [demoFixture, setDemoFixture] = useState(null);
  const [demoFixtureStatus, setDemoFixtureStatus] = useState('idle'); // idle|loading|ready|error
  const [activeWebSessionDemo, setActiveWebSessionDemo] = useState(null); // demo_context echoed by server
  const demoReqSeqRef = useRef(0); // fences late fixture responses

  // Audio input settings
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState('');
  const [rawMicMode, setRawMicMode] = useState(false); // false = enable echo cancellation + noise suppression (recommended for client-side BG noise)
  const [inputGain, setInputGain] = useState(3); // 1x to 5x gain

  const [transcriptModal, setTranscriptModal] = useState({
    isOpen: false,
    callId: null,
    transcript: null,
    loading: false
  });

  // Agent tools preview (web calls)
  const [agentTools, setAgentTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsVerified, setToolsVerified] = useState({}); // { toolId: 'ok' | 'error' | 'checking' }

  // Webhook data preview (web calls)
  const [webhookData, setWebhookData] = useState(null); // { total_records, fields, preview }
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookPresets, setWebhookPresets] = useState([]);

  // Data Dictionary (field descriptions for AI context)
  const [fieldDescriptions, setFieldDescriptions] = useState({});
  const [fieldFormats, setFieldFormats] = useState({});
  const [fieldTemplates, setFieldTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef(null);

  // WebSocket and audio refs for web calls
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const activeSourcesRef = useRef([]); // para poder stop() todo si hay interrupción
  const isAssistantSpeakingRef = useRef(false); // mute mic while bot speaks
  // Jitter buffer: accumulate chunks before starting playback
  const audioQueueRef = useRef([]);
  const isPlaybackStartedRef = useRef(false);
  const preBufferTimerRef = useRef(null);
  const PRE_BUFFER_CHUNKS = 2;
  const PRE_BUFFER_TIMEOUT_MS = 300;
  const processorRef = useRef(null); // for mic reacquire
  const sourceNodeRef = useRef(null); // for mic reacquire
  const isReacquiringRef = useRef(false); // prevent multiple reacquires

  // Client-side background noise refs
  const bgAudioBufferRef = useRef(null);
  const bgSourceNodeRef = useRef(null);
  const bgGainNodeRef = useRef(null);
  const userSpeakingRef = useRef(false);

  // Enumerate audio input devices on mount
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        // Need to request permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ audio: true }).then(s => s.getTracks().forEach(t => t.stop()));
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        console.log('[WebCall] Available mics:', audioInputs.map(d => ({ id: d.deviceId, label: d.label })));
        setAudioDevices(audioInputs);
        // Select first non-default device if available
        if (audioInputs.length > 1) {
          const nonDefault = audioInputs.find(d => d.deviceId !== 'default' && d.deviceId !== 'communications');
          if (nonDefault) setSelectedMicId(nonDefault.deviceId);
        }
      } catch (e) {
        console.error('[WebCall] Failed to enumerate devices:', e);
      }
    };
    enumerateDevices();
  }, []);

  useEffect(() => {
    fetchData();

    // Resume AudioContext when tab becomes visible (after Slack/Discord call)
    const handleVisibilityChange = () => {
      if (!document.hidden && audioContextRef.current) {
        console.log('[WebCall] Tab visible, checking AudioContext...');
        resumeAudioContext();
      }
    };

    // Reacquire mic when audio devices change (Slack/Discord switched default)
    const handleDeviceChange = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.warn('[WebCall] Audio device changed -> reacquiring mic');
        safeReacquireMic();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      // Cleanup listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      // Cleanup web call on unmount
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Close template dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [phoneCallsResponse, webCallsResponse, agentsResponse, phoneNumbersResponse, presetsResponse, templatesResponse] = await Promise.all([
        api.get('/phone-calls/sessions'),
        api.get('/web-calls/sessions').catch(() => ({ data: { sessions: [] } })),
        api.get('/agents'),
        api.get('/phone-numbers'),
        api.get('/webhook-presets').catch(() => ({ data: [] })),
        listFieldTemplates().catch(() => ({ data: [] })),
      ]);

      setPhoneCalls(phoneCallsResponse.data.sessions || []);
      setWebCalls(webCallsResponse.data.sessions || []);
      setFromNumbers(phoneNumbersResponse.data || []);
      setWebhookPresets(presetsResponse.data || []);
      setFieldTemplates(templatesResponse.data || []);
      // Auto-select first from number if not set
      if (!phoneCallForm.twilio_phone_number && phoneNumbersResponse.data?.length > 0) {
        setPhoneCallForm(prev => ({ ...prev, twilio_phone_number: phoneNumbersResponse.data[0].number }));
      }
      setAgents(agentsResponse.data.filter(agent => agent.is_active));
    } catch (error) {
      toast.error(t('errors.failedFetchCallData'));
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load tools for selected agent
  const fetchAgentTools = async (agentId) => {
    if (!agentId) { setAgentTools([]); return; }
    try {
      setToolsLoading(true);
      setToolsVerified({});
      const [agentRes, toolsRes] = await Promise.all([
        api.get(`/agents/${agentId}`),
        api.get('/tools'),
      ]);
      const agentToolIds = agentRes.data.tools || [];
      const allTools = toolsRes.data || [];
      const matched = allTools.filter(t => agentToolIds.includes(t.id));
      setAgentTools(matched);
    } catch (error) {
      console.error('Error fetching agent tools:', error);
      setAgentTools([]);
    } finally {
      setToolsLoading(false);
    }
  };

  // Demo data toggle. Every change bumps the sequence so an in-flight fixture
  // response from an earlier ON (or before an agent/tenant change) is discarded.
  const applyWebDemoMode = async (on) => {
    const seq = ++demoReqSeqRef.current;
    if (!on) {
      setWebDemoMode(false);
      setDemoFixture(null);
      setDemoFixtureStatus('idle');
      return;
    }
    setWebDemoMode(true);
    setDemoFixture(null);
    setDemoFixtureStatus('loading');
    try {
      const res = await api.get(DEMO_FIXTURE_PATH);
      if (seq !== demoReqSeqRef.current) return;
      if (isUsableDemoFixture(res?.data)) {
        setDemoFixture(res.data);
        setDemoFixtureStatus('ready');
      } else {
        setDemoFixtureStatus('error');
      }
    } catch (err) {
      if (seq !== demoReqSeqRef.current) return;
      console.warn('[WebCall][DEMO] fixture unavailable:', err?.response?.status || err?.message);
      setDemoFixtureStatus('error');
    }
  };

  // Agent or organization change -> demo data OFF (explicit re-opt-in per agent).
  const prevDemoScopeRef = useRef({ agent: '', tenant: tenantKey });
  useEffect(() => {
    const prev = prevDemoScopeRef.current;
    const changed = prev.agent !== webCallForm.agent_id || prev.tenant !== tenantKey;
    prevDemoScopeRef.current = { agent: webCallForm.agent_id, tenant: tenantKey };
    if (changed && webDemoMode) {
      toast(t('calls.demo.resetAgent'));
      applyWebDemoMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webCallForm.agent_id, tenantKey]);

  // Verify a single tool's webhook
  const verifyToolWebhook = async (toolId) => {
    setToolsVerified(prev => ({ ...prev, [toolId]: 'checking' }));
    try {
      const res = await api.post(`/tools/${toolId}/test`);
      setToolsVerified(prev => ({ ...prev, [toolId]: res.data?.success ? 'ok' : 'error' }));
    } catch {
      setToolsVerified(prev => ({ ...prev, [toolId]: 'error' }));
    }
  };

  // Verify all tools
  const verifyAllTools = () => {
    agentTools.filter(t => t.webhook_url).forEach(t => verifyToolWebhook(t.id));
  };

  // Load webhook data for preview (optionally pass a URL directly, e.g. from preset)
  const handleLoadWebhook = async (urlOverride) => {
    const url = (urlOverride || webCallForm.test_webhook_url || phoneCallForm.test_webhook_url || '').trim();
    if (!url) return;
    setWebhookLoading(true);
    try {
      const res = await api.post('/web-calls/load-webhook', {
        webhook_url: url,
        default_region: 'MX',
      });
      setWebhookData(res.data);
      if (res.data.valid_count > 0) {
        toast.success(t('success.validContactsFetched', { count: res.data.valid_count }));
      } else {
        toast(t('calls.noDataReturned'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedLoadWebhook'));
      setWebhookData(null);
      setFieldDescriptions({});
      setFieldFormats({});
    } finally {
      setWebhookLoading(false);
    }
  };

  // Audio playback helpers
  const ensureAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      nextPlayTimeRef.current = audioContextRef.current.currentTime;
    }
    // Si el contexto está suspendido (autoplay policy), reanudar
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
  };

  const resetPlaybackQueue = () => {
    try {
      // detener todos los sources en cola
      activeSourcesRef.current.forEach((s) => {
        try { s.stop(); } catch (_) {}
        try { s.disconnect(); } catch (_) {}
      });
    } finally {
      activeSourcesRef.current = [];
      audioQueueRef.current = [];
      isPlaybackStartedRef.current = false;
      if (preBufferTimerRef.current) {
        clearTimeout(preBufferTimerRef.current);
        preBufferTimerRef.current = null;
      }
      isAssistantSpeakingRef.current = false;
      setIsAgentSpeaking(false);
      // Restore BG noise to idle volume after interruption
      duckBackgroundNoise(0.5, 200);
      if (audioContextRef.current) {
        nextPlayTimeRef.current = audioContextRef.current.currentTime + 0.02;
      } else {
        nextPlayTimeRef.current = 0;
      }
    }
  };

  // Resume AudioContext if suspended (happens when switching apps/calls)
  const resumeAudioContext = async () => {
    const ctx = audioContextRef.current;
    if (ctx && ctx.state === 'suspended') {
      console.log('[WebCall] Resuming suspended AudioContext');
      try { await ctx.resume(); } catch (e) { console.warn('[WebCall] Failed to resume AudioContext:', e); }
    }
  };

  // Reacquire mic without closing WebSocket (for when Slack/Discord steal the mic)
  const safeReacquireMic = async () => {
    if (isReacquiringRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    isReacquiringRef.current = true;
    console.log('[WebCall] Reacquiring microphone...');

    try {
      // Stop old tracks
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      // Disconnect old nodes
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.disconnect(); } catch (_) {}
      }

      // Get new stream with same constraints
      const audioConstraints = {
        ...(selectedMicId && { deviceId: { exact: selectedMicId } }),
        echoCancellation: !rawMicMode,
        noiseSuppression: !rawMicMode,
        autoGainControl: !rawMicMode,
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      mediaStreamRef.current = stream;

      // Reconnect to existing AudioContext and processor
      if (audioContextRef.current && processorRef.current) {
        await resumeAudioContext();
        sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(stream);
        sourceNodeRef.current.connect(processorRef.current);
        console.log('[WebCall] Mic reacquired and reconnected');
      }

      // Attach watchdog to new track
      attachMicWatchdog(stream);
    } catch (e) {
      console.error('[WebCall] Failed to reacquire mic:', e);
    } finally {
      isReacquiringRef.current = false;
    }
  };

  // Watchdog for mic track - detects when Slack/Discord steal it
  const attachMicWatchdog = (stream) => {
    const [track] = stream.getAudioTracks();
    if (!track) return;

    console.log('[WebCall] Mic watchdog attached:', track.label);

    track.onended = () => {
      console.warn('[WebCall] Mic track ended -> reacquiring');
      safeReacquireMic();
    };
    track.onmute = () => console.warn('[WebCall] Mic muted by system');
    track.onunmute = () => console.warn('[WebCall] Mic unmuted by system');
  };

  // --- Client-side background noise ---
  const startBackgroundNoise = () => {
    if (!bgAudioBufferRef.current || !audioContextRef.current) return;
    try {
      stopBackgroundNoise(); // ensure no double-play
      const ctx = audioContextRef.current;
      const source = ctx.createBufferSource();
      source.buffer = bgAudioBufferRef.current;
      source.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0.5; // 50% — audible call center ambiance
      source.connect(gain);
      gain.connect(ctx.destination);
      source.start(0);
      bgSourceNodeRef.current = source;
      bgGainNodeRef.current = gain;
      console.log('[WebCall] Background noise started (gain=0.5)');
    } catch (e) {
      console.warn('[WebCall] Failed to start background noise:', e);
    }
  };

  const stopBackgroundNoise = () => {
    if (bgSourceNodeRef.current) {
      try { bgSourceNodeRef.current.stop(); } catch (_) {}
      try { bgSourceNodeRef.current.disconnect(); } catch (_) {}
      bgSourceNodeRef.current = null;
    }
    if (bgGainNodeRef.current) {
      try { bgGainNodeRef.current.disconnect(); } catch (_) {}
      bgGainNodeRef.current = null;
    }
  };

  const duckBackgroundNoise = (targetGain, rampTimeMs = 300) => {
    if (!bgGainNodeRef.current || !audioContextRef.current) return;
    try {
      const gain = bgGainNodeRef.current.gain;
      const ctx = audioContextRef.current;
      gain.cancelScheduledValues(ctx.currentTime);
      gain.linearRampToValueAtTime(targetGain, ctx.currentTime + rampTimeMs / 1000);
    } catch (_) {}
  };

  const handleInitiatePhoneCall = async (e) => {
    e.preventDefault();

    if (!phoneCallForm.agent_id || !phoneCallForm.phone_number) {
      toast.error(t('errors.selectAgentAndNumber'));
      return;
    }

    try {
      setInitiatingCall(true);
      const payload = {
        agent_id: phoneCallForm.agent_id,
        phone_number: phoneCallForm.phone_number,
        twilio_phone_number: phoneCallForm.twilio_phone_number,
      };
      if (phoneCallForm.gender_switch) {
        payload.gender_switch = true;
      }
      if (phoneCallForm.test_webhook_url) {
        payload.test_webhook_url = phoneCallForm.test_webhook_url;
      }
      if (phoneCallForm.handoff_phone_number) {
        payload.handoff_phone_number = phoneCallForm.handoff_phone_number;
      }
      // salesperson_id removed — agent owner_user_id handles attribution
      // Send webhook context (first valid contact) so the agent has contact data
      if (webhookData?.valid_preview?.length > 0) {
        const contact = webhookData.valid_preview[0];
        const { context: ctx, ...rest } = contact;
        payload.webhook_context = { ...rest, ...(ctx || {}) };
      }
      // Send field descriptions
      if (Object.values(fieldDescriptions).some(v => v?.trim())) {
        payload.field_descriptions = fieldDescriptions;
      }
      // Send field formats
      const nonDefaultFormats = Object.fromEntries(
        Object.entries(fieldFormats).filter(([, v]) => v && v !== 'none')
      );
      if (Object.keys(nonDefaultFormats).length > 0) {
        payload.field_formats = nonDefaultFormats;
      }
      console.log('[PHONE CALL] Payload:', { gender_switch: payload.gender_switch, field_formats: payload.field_formats || '(none)', webhook_context_keys: Object.keys(payload.webhook_context || {}) });
      await api.post('/phone-calls/initiate', payload);

      toast.success(t('success.callInitiated'));
      setPhoneCallForm(prev => ({ ...prev, phone_number: '', handoff_phone_number: '' }));
      fetchData();
    } catch (error) {
      toast.error(t('errors.failedInitiateCall'));
      console.error('Error initiating phone call:', error);
    } finally {
      setInitiatingCall(false);
    }
  };

  const handleInitiateWebCall = async (e) => {
    e.preventDefault();

    if (!webCallForm.agent_id) {
      toast.error(t('errors.selectAgent'));
      return;
    }
    // Snapshot demo intent at submit time; toggle is disabled while initiating.
    const requestDemo = webDemoMode;
    if (requestDemo && demoFixtureStatus !== 'ready') {
      toast.error(t('calls.demo.unavailableServer'));
      return;
    }

    try {
      setInitiatingCall(true);

      // Build audio constraints based on settings
      const audioConstraints = {
        // Use specific deviceId if selected (not "default")
        ...(selectedMicId && { deviceId: { exact: selectedMicId } }),
        // Raw mode disables all processing for diagnosis
        echoCancellation: !rawMicMode,
        noiseSuppression: !rawMicMode,
        autoGainControl: !rawMicMode,
      };
      console.log('[WebCall] Audio constraints:', audioConstraints);

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      mediaStreamRef.current = stream;

      // Log track settings for debugging
      const [track] = stream.getAudioTracks();
      if (track) {
        const settings = track.getSettings();
        console.log('[WebCall] Track settings:', {
          deviceId: settings.deviceId,
          label: track.label,
          sampleRate: settings.sampleRate,
          channelCount: settings.channelCount,
          echoCancellation: settings.echoCancellation,
          noiseSuppression: settings.noiseSuppression,
          autoGainControl: settings.autoGainControl,
        });
      }

      // Initiate web call session. Demo ON -> only agent_id + demo_mode (the
      // server substitutes its fictional fixture); OFF -> the existing webhook
      // payload (first valid contact flattened, field descriptions/formats).
      const payload = buildWebCallPayload({
        agentId: webCallForm.agent_id,
        testWebhookUrl: webCallForm.test_webhook_url,
        genderSwitch: webCallForm.gender_switch,
        demoMode: requestDemo,
        webhookData,
        fieldDescriptions,
        fieldFormats,
      });
      console.log('[WEB CALL] Payload:', { demo_mode: !!payload.demo_mode, gender_switch: payload.gender_switch, field_formats: payload.field_formats || '(none)', webhook_context_keys: Object.keys(payload.webhook_context || {}) });
      const response = await api.post('/web-calls/initiate', payload);

      if (!demoEchoConfirmed(requestDemo, response.data)) {
        // Older backend dropped demo_mode -> this would be an UNFENCED real
        // session. Do not open the WebSocket; release the microphone.
        stream.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
        toast.error(t('calls.demo.backendNoDemo'));
        return;
      }

      const sessionId = response.data.session_id;
      setActiveWebSession(sessionId);
      setActiveWebSessionDemo(requestDemo ? response.data.demo_context : null);

      // Build WebSocket URL
      // If REACT_APP_WS_URL is set (proxy mode), use it directly
      // Otherwise derive from API_BASE_URL
      let wsUrl;
      const WS_BASE = process.env.REACT_APP_WS_URL;
      if (WS_BASE) {
        // Proxy mode: use explicit WS URL
        wsUrl = `${WS_BASE}/ws/web-call/${sessionId}`;
      } else if (API_BASE_URL.startsWith('http://') || API_BASE_URL.startsWith('https://')) {
        // Absolute API URL configured at build time
        const wsProtocol = API_BASE_URL.startsWith('https://') ? 'wss:' : 'ws:';
        const wsHost = API_BASE_URL
          .replace(/^https?:\/\//, '')  // Remove http:// or https://
          .replace(/\/api$/, '');        // Remove /api suffix
        wsUrl = `${wsProtocol}//${wsHost}/ws/web-call/${sessionId}`;
      } else {
        // Same-origin deployment (API_BASE_URL is relative like /api)
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProtocol}//${window.location.host}/ws/web-call/${sessionId}`;
      }

      console.log('[WebCall] API_BASE_URL:', API_BASE_URL);
      console.log('[WebCall] WS_BASE:', WS_BASE || '(derived from API)');
      console.log('[WebCall] Constructed WebSocket URL:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebCall] ✅ WebSocket connected successfully');
        setWebCallActive(true);
        toast.success(t('success.webCallConnected'));
        startAudioStreaming(stream, ws);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WebCall] Message received:', data.type);
          if (data.type === 'audio') {
            playAudio(data.audio);
          } else if (data.type === 'error') {
            console.error('[WebCall] Server error:', data.message);
            toast.error(data.message || t('errors.serverError'));
          } else if (data.type === 'connected') {
            console.log('[WebCall] Session confirmed connected by server');
          } else if (data.type === 'interrupted' || data.type === 'response.started' || data.type === 'speech_started') {
            // Reset audio queue on interruption events
            console.log('[WebCall] Interruption event, resetting audio queue');
            resetPlaybackQueue();
          } else if (data.type === 'call_ended') {
            // Server-initiated hangup (inactivity timeout, error, etc.)
            console.log('[WebCall] Server ended call, reason:', data.reason);
            toast.info(data.reason === 'inactivity_timeout'
              ? t('calls.callEndedInactivity')
              : t('calls.callEndedReason', { reason: data.reason }));
            // Explicit cleanup - don't rely on onclose firing
            // The server will close the WS after this, but we clean up proactively
            handleEndWebCall(`server_${data.reason}`);
          }
        } catch (e) {
          console.error('[WebCall] Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('[WebCall] WebSocket closed: code=', event.code, 'reason=', event.reason, 'wasClean=', event.wasClean);

        // Check if cleanup was already done by handleEndWebCall
        if (!wsRef.current) {
          console.log('[WebCall] onclose: cleanup already done, skipping');
          return;
        }

        resetPlaybackQueue();
        stopBackgroundNoise();
        bgAudioBufferRef.current = null;
        userSpeakingRef.current = false;

        // Full cleanup on WS close
        if (sourceNodeRef.current) {
          try { sourceNodeRef.current.disconnect(); } catch (_) {}
          sourceNodeRef.current = null;
        }
        if (processorRef.current) {
          try { processorRef.current.disconnect(); } catch (_) {}
          processorRef.current = null;
        }
        if (audioContextRef.current) {
          try { audioContextRef.current.close(); } catch (_) {}
          audioContextRef.current = null;
        }
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
          mediaStreamRef.current = null;
        }
        wsRef.current = null;
        nextPlayTimeRef.current = 0;
        isReacquiringRef.current = false;

        setWebCallActive(false);
        setActiveWebSession(null);
        setActiveWebSessionDemo(null);

        // Show message based on close code
        if (event.code === 4000) {
          // Server-initiated hangup (inactivity) - toast was already shown by call_ended handler
          console.log('[WebCall] Server hangup detected via close code 4000');
        } else if (event.code !== 1000) {
          // Unexpected close
          toast.error(t('calls.connectionLost', { code: event.code }));
        }
      };

      ws.onerror = (error) => {
        console.error('[WebCall] ❌ WebSocket error:', error);
        console.error('[WebCall] WebSocket readyState:', ws.readyState);
        toast.error(t('errors.webCallError'));
      };

      // Don't call fetchData() here - it can cause CORS errors and interrupt the call
    } catch (error) {
      toast.error(t('errors.failedInitiateWebCall'));
      console.error('Error initiating web call:', error);
      setWebCallActive(false);
      setActiveWebSessionDemo(null);
    } finally {
      setInitiatingCall(false);
    }
  };

  const startAudioStreaming = async (stream, ws) => {
    try {
      // Create AudioContext - browser may give us 48kHz even if we request 24kHz
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      const actualSampleRate = audioContextRef.current.sampleRate;
      console.log('[WebCall] AudioContext sampleRate:', actualSampleRate);

      // Load background noise MP3 for client-side playback
      try {
        const bgResp = await fetch('/audio/backgroundNoise.mp3');
        const bgBuf = await bgResp.arrayBuffer();
        bgAudioBufferRef.current = await audioContextRef.current.decodeAudioData(bgBuf);
        console.log('[WebCall] Background noise loaded:', bgAudioBufferRef.current.duration.toFixed(1), 's');
      } catch (bgErr) {
        console.warn('[WebCall] Failed to load background noise:', bgErr);
      }

      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);

      // Store refs for mic reacquire
      sourceNodeRef.current = source;
      processorRef.current = processor;

      // Attach watchdog to detect when Slack/Discord steal the mic
      attachMicWatchdog(stream);

      // Downsample function for 48k -> 24k (take every 2nd sample)
      const downsample48kTo24k = (float32) => {
        const out = new Float32Array(Math.floor(float32.length / 2));
        for (let i = 0, j = 0; j < out.length; i += 2, j++) {
          out[j] = float32[i];
        }
        return out;
      };

      // RMS level calculation for noise gate
      const rmsLevel = (float32) => {
        let sum = 0;
        for (let i = 0; i < float32.length; i++) sum += float32[i] * float32[i];
        return Math.sqrt(sum / float32.length);
      };

      // Noise gate threshold — gates on the POST-GAIN effective level the way OpenAI
      // actually hears it. The old gate compared pre-gain `level` to 0.008 while audio
      // is sent x inputGain (3x default), so pre-gain frames in [0.008, ~0.0073) — i.e.
      // low background noise — amplified to ~0.024-0.045 post-gain and false-fired
      // OpenAI semantic_vad speech_started, producing premature commits / slow answers.
      // Gate on (level * inputGain) against a post-gain threshold instead.
      const POST_GAIN_NOISE_GATE = 0.022;
      // Hysteresis: once the gate opens (real speech), keep it open for a brief hold so
      // natural mid-word dips don't chop a turn; closing again still delivers clean
      // silence zeros (preserves PR #661 dead-air fix). Hold <= 2 frames (~340ms).
      const GATE_OPEN_MARGIN = 0.004; // close threshold a touch lower than open (hysteresis band)
      const GATE_HOLD_FRAMES = 2;
      // User-speaking detection threshold (for ducking)
      const SPEECH_THRESHOLD = 0.015;
      // Silent frames counter for debounced "user stopped speaking" (~5 frames = ~850ms)
      const SILENCE_DEBOUNCE_FRAMES = 5;

      let audioSentCount = 0;
      let silentFrames = 0;
      let silentFrameCounter = 0; // for speech debounce
      let gateOpen = false;       // hysteresis state
      let gateHoldRemaining = 0;  // frames the gate stays open after dropping below threshold
      processor.onaudioprocess = (e) => {
        if (ws.readyState === WebSocket.OPEN) {
          let inputData = e.inputBuffer.getChannelData(0);
          const level = rmsLevel(inputData);

          // --- Ducking: detect user speaking for BG noise volume control ---
          if (level > SPEECH_THRESHOLD) {
            if (!userSpeakingRef.current) {
              userSpeakingRef.current = true;
              duckBackgroundNoise(0.15, 200); // duck when user speaks
            }
            silentFrameCounter = 0;
          } else {
            silentFrameCounter++;
            if (userSpeakingRef.current && silentFrameCounter >= SILENCE_DEBOUNCE_FRAMES) {
              userSpeakingRef.current = false;
              // Restore gain based on whether AI is speaking
              duckBackgroundNoise(isAssistantSpeakingRef.current ? 0.25 : 0.5, 400);
            }
          }

          // Debug: log RMS every 2 seconds (~50 frames at 4096 samples/24kHz)
          if ((audioSentCount + silentFrames) % 50 === 0) {
            let postGainRms = 0;
            for (let i = 0; i < inputData.length; i++) {
              const gained = Math.max(-1, Math.min(1, inputData[i] * inputGain));
              postGainRms += gained * gained;
            }
            postGainRms = Math.sqrt(postGainRms / inputData.length);
            console.log('[WebCall] RMS pre:', level.toFixed(4), 'post-gain:', postGainRms.toFixed(4), 'gain:', inputGain, 'speaking:', isAssistantSpeakingRef.current, 'userSpeaking:', userSpeakingRef.current);
          }

          // --- Noise gate: send silence unless the POST-GAIN level is real speech ---
          // Compare the effective level OpenAI receives (level * inputGain) against a
          // post-gain threshold, with open/close hysteresis so a brief mid-word dip
          // doesn't slam the gate shut. When the gate is closed we still deliver clean
          // silence zeros (preserves PR #661 dead-air fix: OpenAI's VAD sees a real
          // silence window and emits speech_stopped).
          const effectiveLevel = level * inputGain;
          if (gateOpen) {
            // Stay open until we fall below the (lower) close threshold for the hold window.
            if (effectiveLevel >= POST_GAIN_NOISE_GATE - GATE_OPEN_MARGIN) {
              gateHoldRemaining = GATE_HOLD_FRAMES;
            } else if (gateHoldRemaining > 0) {
              gateHoldRemaining--;
            } else {
              gateOpen = false;
            }
          } else if (effectiveLevel >= POST_GAIN_NOISE_GATE) {
            gateOpen = true;
            gateHoldRemaining = GATE_HOLD_FRAMES;
          }

          if (!gateOpen) {
            silentFrames++;
            // Send silent PCM16 frame
            const sampleCount = actualSampleRate === 48000 ? Math.floor(inputData.length / 2) : inputData.length;
            const silentPcm16 = new Int16Array(sampleCount); // zero-filled
            const base64Audio = btoa(String.fromCharCode(...new Uint8Array(silentPcm16.buffer)));
            ws.send(JSON.stringify({ type: 'audio', audio: base64Audio }));
            return;
          }

          audioSentCount++;
          if (audioSentCount % 50 === 0) console.log('[WebCall] Sending audio, RMS:', level.toFixed(4));

          // Resample if needed (48k -> 24k)
          if (actualSampleRate === 48000) {
            inputData = downsample48kTo24k(inputData);
          }

          // Apply input gain (boost weak mic signal)
          const gainedData = new Float32Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            gainedData[i] = Math.max(-1, Math.min(1, inputData[i] * inputGain));
          }

          // Convert to PCM16
          const pcm16 = new Int16Array(gainedData.length);
          for (let i = 0; i < gainedData.length; i++) {
            pcm16[i] = Math.max(-32768, Math.min(32767, gainedData[i] * 32768));
          }

          const base64Audio = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));
          ws.send(JSON.stringify({ type: 'audio', audio: base64Audio }));
        }
      };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      // Start client-side background noise loop
      startBackgroundNoise();
    } catch (error) {
      console.error('Error starting audio streaming:', error);
    }
  };

  // Schedule a single decoded audio buffer for playback
  const scheduleBuffer = (audioBuffer) => {
    const ctx = audioContextRef.current;
    if (!ctx) return;

    const now = ctx.currentTime;
    if (nextPlayTimeRef.current < now) {
      nextPlayTimeRef.current = now + 0.005;
    }

    const startTime = nextPlayTimeRef.current;
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    activeSourcesRef.current.push(source);
    isAssistantSpeakingRef.current = true;
    setIsAgentSpeaking(true);
    duckBackgroundNoise(0.25, 200);

    source.onended = () => {
      activeSourcesRef.current = activeSourcesRef.current.filter((s) => s !== source);
      try { source.disconnect(); } catch (_) {}
      if (activeSourcesRef.current.length === 0) {
        isAssistantSpeakingRef.current = false;
        setIsAgentSpeaking(false);
        duckBackgroundNoise(0.5, 400);
      }
    };

    source.start(startTime);
    nextPlayTimeRef.current = startTime + audioBuffer.duration;
  };

  // Drain the pre-buffer queue and start continuous playback
  const drainAudioQueue = () => {
    isPlaybackStartedRef.current = true;
    const queued = audioQueueRef.current;
    audioQueueRef.current = [];
    for (const buf of queued) {
      scheduleBuffer(buf);
    }
  };

  const playAudio = async (base64Audio) => {
    try {
      ensureAudioContext();
      const ctx = audioContextRef.current;

      // Decode base64 -> bytes -> PCM16 -> Float32
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768;
      }

      const audioBuffer = ctx.createBuffer(1, float32.length, 24000);
      audioBuffer.getChannelData(0).set(float32);

      if (!isPlaybackStartedRef.current) {
        // Pre-buffering phase: queue chunks before starting playback
        audioQueueRef.current.push(audioBuffer);

        // Start timeout on first chunk
        if (audioQueueRef.current.length === 1) {
          preBufferTimerRef.current = setTimeout(() => {
            preBufferTimerRef.current = null;
            drainAudioQueue();
          }, PRE_BUFFER_TIMEOUT_MS);
        }

        // If enough chunks accumulated, start immediately
        if (audioQueueRef.current.length >= PRE_BUFFER_CHUNKS) {
          if (preBufferTimerRef.current) {
            clearTimeout(preBufferTimerRef.current);
            preBufferTimerRef.current = null;
          }
          drainAudioQueue();
        }
      } else {
        // Already playing — schedule immediately
        scheduleBuffer(audioBuffer);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const handleEndWebCall = (reason = 'unknown') => {
    // Prevent double cleanup
    if (!webCallActive && !wsRef.current) {
      console.log('[WebCall] Already cleaned up, skipping');
      return;
    }

    console.log('[WebCall] Ending call, cleaning up... reason=', reason);
    resetPlaybackQueue();

    // Stop background noise
    stopBackgroundNoise();
    bgAudioBufferRef.current = null;
    userSpeakingRef.current = false;

    // Disconnect audio nodes
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.disconnect(); } catch (_) {}
      sourceNodeRef.current = null;
    }
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch (_) {}
      processorRef.current = null;
    }

    // Close AudioContext
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (_) {}
      audioContextRef.current = null;
    }

    // Close WebSocket if still open
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null; // Clear ref first to prevent onclose re-entry
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, reason);
      }
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Reset all refs
    nextPlayTimeRef.current = 0;
    isReacquiringRef.current = false;

    setWebCallActive(false);
    setActiveWebSession(null);
    setActiveWebSessionDemo(null);

    // Only show toast for user-initiated hangup (not server-initiated)
    if (!reason.startsWith('server_')) {
      toast.success(t('success.webCallEnded'));
    }
    fetchData();
  };

  const handleViewTranscript = async (callId) => {
    try {
      setTranscriptModal(prev => ({ ...prev, isOpen: true, callId, loading: true }));

      const response = await api.get(`/transcripts/${callId}`);

      setTranscriptModal(prev => ({
        ...prev,
        transcript: response.data.transcript || response.data,
        loading: false
      }));
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        // No transcript yet — show empty state, no toast
        setTranscriptModal(prev => ({ ...prev, loading: false, transcript: { transcript: [] } }));
      } else if (status === 401) {
        // Session expired — show empty state with re-auth hint
        toast.error(t('errors.sessionExpired', 'Session expired — please refresh and try again'));
        setTranscriptModal(prev => ({ ...prev, loading: false, transcript: { transcript: [] } }));
      } else {
        // 403, 500, network — graceful empty-state instead of hard error
        if (status === 403) toast.error(t('errors.accessDenied', 'Access denied'));
        setTranscriptModal(prev => ({ ...prev, loading: false, transcript: { transcript: [] } }));
      }
      console.error('Error loading transcript:', error);
    }
  };

  const closeTranscriptModal = () => {
    setTranscriptModal({
      isOpen: false,
      callId: null,
      transcript: null,
      loading: false
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
      case 'initiated':
        return <PlayIcon className="h-5 w-5 text-primary-500" />;
      case 'ended':
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-success-500" />;
      default:
        return <ClockIcon className="h-5 w-5" style={{ color: 'var(--muted)' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
      case 'initiated':
        return 'status-calling';
      case 'ended':
      case 'completed':
        return 'status-active';
      default:
        return 'status-inactive';
    }
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : 'Unknown Agent';
  };

  const formatDuration = (startedAt, endedAt) => {
    if (!endedAt) return t('calls.inProgress');

    const start = new Date(startedAt);
    const end = new Date(endedAt);
    const duration = Math.floor((end - start) / 1000);

    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const currentCalls = activeTab === 'web' ? webCalls : phoneCalls;
  const allCalls = [...phoneCalls, ...webCalls];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
            {t('calls.title')}
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {t('calls.subtitle')}
          </p>
        </div>
      </div>

      <div className="inline-flex rounded-xl border border-border bg-card p-1">
        <button
          type="button"
          onClick={() => setActiveTab('web')}
          className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'web'
              ? 'bg-primary text-white shadow-sm'
              : 'text-foreground hover:bg-background'
          }`}
        >
          <GlobeAltIcon className="mr-2 h-4 w-4" />
          {t('calls.webCalls')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('phone')}
          className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'phone'
              ? 'bg-primary text-white shadow-sm'
              : 'text-foreground hover:bg-background'
          }`}
        >
          <PhoneIcon className="mr-2 h-4 w-4" />
          {t('calls.phoneCalls')}
        </button>
      </div>

      {/* Phone Call Form */}
      {activeTab === 'phone' && (
        <div className="card">
          <h3 className="text-lg font-medium text-foreground mb-4">
            <PhoneIcon className="h-5 w-5 inline mr-2" />
            {t('calls.initiatePhoneCall')}
          </h3>
          <form onSubmit={handleInitiatePhoneCall} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="phone_agent_id" className="block text-sm font-medium text-foreground">
                  {t('calls.selectAgent')}
                </label>
                <select
                  id="phone_agent_id"
                  value={phoneCallForm.agent_id}
                  onChange={(e) => setPhoneCallForm(prev => ({ ...prev, agent_id: e.target.value }))}
                  className="input-field mt-1"
                  required
                >
                  <option value="">{t('calls.chooseAgent')}</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}{agent.is_shared ? ` (${t('agents.shared')})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="phone_number" className="block text-sm font-medium text-foreground">
                  {t('calls.phoneNumber')}
                </label>
                <PhoneInput
                  id="phone_number"
                  value={phoneCallForm.phone_number}
                  onChange={(value) => setPhoneCallForm(prev => ({ ...prev, phone_number: value }))}
                  defaultCountry="MX"
                  storageKey="saved_destination_numbers"
                  placeholder="1234567890"
                  required
                />
              </div>

              <div>
                <label htmlFor="twilio_phone_number" className="block text-sm font-medium text-foreground">
                  {t('calls.fromNumber')}
                </label>
                <select
                  id="twilio_phone_number"
                  value={phoneCallForm.twilio_phone_number}
                  onChange={(e) => setPhoneCallForm(prev => ({ ...prev, twilio_phone_number: e.target.value }))}
                  className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                >
                  <option value="">{t('phoneNumbers.selectFromNumber')}</option>
                  {fromNumbers.map((num) => (
                    <option key={num.number} value={num.number}>
                      {num.number} {num.label ? `- ${num.label}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* transfer number input removed — handoff number comes from the child agent config */}

              {/* salesperson dropdown removed - agent owner_user_id handles attribution */}
            </div>

            {/* Test Webhook URL for phone calls */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('calls.testWebhookUrl')}
              </label>

              {webhookData && webhookData.valid_count > 0 ? (
                /* Locked state (data loaded) */
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={phoneCallForm.test_webhook_url || ''}
                      className="flex-1 px-3 py-2.5 bg-secondary border border-green-300 rounded-lg text-sm font-mono cursor-default"
                      style={{ color: 'var(--muted)' }}
                    />
                    <button
                      type="button"
                      onClick={() => { setWebhookData(null); setFieldDescriptions({}); setFieldFormats({}); }}
                      className="px-3 py-2.5 border border-border rounded-lg text-sm hover:bg-background transition-colors"
                      style={{ color: 'var(--muted)' }}
                      title={t('common.edit')}
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Validation stat cards */}
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                      <p className="text-xl font-semibold text-green-700">{webhookData.valid_count || 0}</p>
                      <p className="text-xs text-green-600">{t('calls.validNumbers')}</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                      <p className="text-xl font-semibold text-red-700">{webhookData.invalid_count || 0}</p>
                      <p className="text-xs text-red-600">{t('calls.invalidNumbers')}</p>
                    </div>
                    {/* Duplicados chip: show rows that SHARE a phone (kept, not dropped)
                        as well as collapsed duplicates. Gran Chapur: rows sharing a
                        number must be surfaced, not reported as "0 Duplicados". */}
                    <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                      <p className="text-xl font-semibold text-yellow-700">
                        {(webhookData.shared_phone_count || 0) > 0
                          ? (webhookData.shared_phone_count || 0)
                          : (webhookData.duplicate_count || 0)}
                      </p>
                      <p className="text-xs text-yellow-600">
                        {(webhookData.shared_phone_count || 0) > 0 && (webhookData.duplicate_count || 0) > 0
                          ? t('calls.duplicatesShared', {
                              shared: webhookData.shared_phone_count || 0,
                              dropped: webhookData.duplicate_count || 0,
                            })
                          : (webhookData.shared_phone_count || 0) > 0
                            ? t('calls.sharedPhones')
                            : t('calls.duplicates')}
                      </p>
                    </div>
                  </div>

                  {/* Phone / Name / Context preview table */}
                  {webhookData.valid_preview?.length > 0 && (
                    <div className="mt-3 border border-green-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-green-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-green-800 text-xs">{t('calls.phone')}</th>
                            <th className="text-left px-3 py-2 font-medium text-green-800 text-xs">{t('calls.contactName')}</th>
                            <th className="text-left px-3 py-2 font-medium text-green-800 text-xs">{t('calls.context')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-green-100">
                          {webhookData.valid_preview.map((c, i) => (
                            <tr key={i} className={c.shared_phone ? 'bg-yellow-50' : 'hover:bg-green-50/50'}>
                              <td className="px-3 py-1.5 font-mono text-xs">
                                {c.phone}
                                {c.shared_phone && (
                                  <span
                                    className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-200 text-yellow-800 align-middle"
                                    title={t('calls.sharedPhoneRowHint')}
                                  >
                                    {t('calls.sharedPhoneBadge')}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-xs">{c.name || '-'}</td>
                              <td className="px-3 py-1.5 text-xs" style={{ color: 'var(--muted)' }}>
                                {c.context && Object.keys(c.context).length > 0
                                  ? Object.keys(c.context).join(', ')
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Data Dictionary — field descriptions for AI context */}
                  {webhookData.valid_preview?.length > 0 && (() => {
                    const topLevelKeys = [];
                    if (webhookData.valid_preview.some(c => c.name)) topLevelKeys.push('name');
                    const contextKeys = [...new Set([
                      ...topLevelKeys,
                      ...webhookData.valid_preview.flatMap(c => c.context ? Object.keys(c.context) : []),
                    ])];
                    if (contextKeys.length === 0) return null;

                    const hasDescriptions = Object.values(fieldDescriptions).some(v => v && v.trim());

                    const handleLoadTemplate = (templateId) => {
                      if (!templateId) return;
                      const tpl = (fieldTemplates || []).find(t => t.template_id === templateId);
                      if (!tpl) return;
                      const merged = { ...fieldDescriptions };
                      Object.entries(tpl.field_descriptions || {}).forEach(([k, v]) => {
                        if (contextKeys.includes(k)) merged[k] = v;
                      });
                      setFieldDescriptions(merged);
                      const mergedFormats = { ...fieldFormats };
                      Object.entries(tpl.field_formats || {}).forEach(([k, v]) => {
                        if (contextKeys.includes(k)) mergedFormats[k] = v;
                      });
                      setFieldFormats(mergedFormats);
                      toast.success(t('campaignWizard.templateLoaded'));
                    };

                    const handleSaveTemplate = async () => {
                      const name = templateName.trim();
                      if (!name) return;
                      setSavingTemplate(true);
                      try {
                        const res = await createFieldTemplate(name, fieldDescriptions, fieldFormats);
                        setFieldTemplates(prev => [...prev, res.data]);
                        setShowSaveTemplate(false);
                        setTemplateName('');
                        toast.success(t('campaignWizard.templateSaved'));
                      } catch (err) {
                        const msg = err.response?.data?.detail || 'Failed to save template';
                        toast.error(msg);
                      } finally {
                        setSavingTemplate(false);
                      }
                    };

                    return (
                      <div className="mt-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                            <DocumentTextIcon className="w-4 h-4" />
                            {t('campaignWizard.dataDictionary')}
                          </h4>
                          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                            {t('campaignWizard.dataDictionaryHelp')}
                          </p>
                        </div>

                        {/* Template Load / Save row */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="relative flex-1" ref={templateDropdownRef}>
                            <button
                              type="button"
                              onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                              className="w-full flex items-center justify-between px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-card"
                            >
                              <span style={{ color: 'var(--muted)' }}>{t('campaignWizard.loadTemplate')}</span>
                              <ChevronDownIcon className="w-3.5 h-3.5" style={{ color: 'var(--muted)' }} />
                            </button>
                            {templateDropdownOpen && (
                              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded shadow-lg max-h-48 overflow-y-auto">
                                {(fieldTemplates || []).length === 0 ? (
                                  <div className="px-3 py-2 text-xs" style={{ color: 'var(--muted)' }}>
                                    {t('campaignWizard.noTemplates')}
                                  </div>
                                ) : (
                                  (fieldTemplates || []).map((tpl) => (
                                    <div
                                      key={tpl.template_id}
                                      className="flex items-center justify-between px-3 py-2 hover:bg-background transition-colors group"
                                    >
                                      <button
                                        type="button"
                                        className="flex-1 text-left text-xs text-foreground truncate"
                                        onClick={() => {
                                          handleLoadTemplate(tpl.template_id);
                                          setTemplateDropdownOpen(false);
                                        }}
                                      >
                                        {tpl.name}
                                        <span className="ml-1" style={{ color: 'var(--muted)' }}>
                                          ({Object.keys(tpl.field_descriptions || {}).length})
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        className="ml-2 p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title={t('common.delete')}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!window.confirm(t('campaignWizard.deleteTemplateConfirm', { name: tpl.name }))) return;
                                          try {
                                            await deleteFieldTemplate(tpl.template_id);
                                            setFieldTemplates(prev => prev.filter(x => x.template_id !== tpl.template_id));
                                            toast.success(t('campaignWizard.templateDeleted'));
                                          } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Failed to delete template');
                                          }
                                        }}
                                      >
                                        <TrashIcon className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>

                          {!showSaveTemplate ? (
                            <button
                              type="button"
                              onClick={() => setShowSaveTemplate(true)}
                              disabled={!hasDescriptions}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              <BookmarkIcon className="w-3.5 h-3.5" />
                              {t('campaignWizard.saveAsTemplate')}
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                placeholder={t('campaignWizard.templateNamePlaceholder')}
                                maxLength={100}
                                className="px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 w-40"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveTemplate();
                                  if (e.key === 'Escape') { setShowSaveTemplate(false); setTemplateName(''); }
                                }}
                              />
                              <button
                                type="button"
                                onClick={handleSaveTemplate}
                                disabled={!templateName.trim() || savingTemplate}
                                className="px-3 py-1.5 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40"
                              >
                                {savingTemplate ? '...' : t('common.save')}
                              </button>
                              <button
                                type="button"
                                onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                                className="p-1" style={{ color: 'var(--muted)' }}
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2 border border-border rounded-xl p-4 max-h-64 overflow-y-auto bg-card">
                          {contextKeys.map((key) => (
                            <div key={key} className="flex items-center gap-3">
                              <span className="text-xs font-mono w-36 flex-shrink-0 truncate" style={{ color: 'var(--muted)' }} title={key}>
                                {key}
                              </span>
                              <input
                                type="text"
                                value={fieldDescriptions[key] || ''}
                                onChange={(e) => {
                                  setFieldDescriptions(prev => ({ ...prev, [key]: e.target.value }));
                                }}
                                placeholder={t('campaignWizard.dataDictionaryPlaceholder')}
                                maxLength={100}
                                className="flex-1 px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-card"
                              />
                              <select
                                value={fieldFormats[key] || 'none'}
                                onChange={(e) => {
                                  setFieldFormats(prev => ({ ...prev, [key]: e.target.value }));
                                }}
                                className="w-32 flex-shrink-0 px-2 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-card"
                                title={t('campaignWizard.fieldFormat', 'Format')}
                              >
                                <option value="none">Auto</option>
                                <option value="name_ap1_ap2_nom1_nom2">Nombre: AP1 AP2 NOM1 NOM2</option>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Editable state — dropdown-only: pick a saved test source (preset). */
                <div className="space-y-2">
                  {webhookPresets.length > 0 ? (
                    <select
                      value=""
                      disabled={webhookLoading}
                      onChange={(e) => {
                        const preset = webhookPresets.find(p => p.id === e.target.value);
                        if (preset) {
                          setPhoneCallForm(prev => ({ ...prev, test_webhook_url: preset.url }));
                          handleLoadWebhook(preset.url);
                        }
                      }}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                    >
                      <option value="">{t('calls.selectWebhookPreset')}</option>
                      {webhookPresets.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {t('calls.noWebhookPresets', 'No hay fuentes de prueba configuradas.')}
                    </p>
                  )}
                  {webhookLoading && (
                    <p className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted)' }}>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" /> {t('calls.loadWebhook')}…
                    </p>
                  )}
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>{t('calls.testWebhookHelp')}</p>
                </div>
              )}
            </div>

            {/* Gender switch toggle — show when agent has secondary voice */}
            {(() => {
              const selectedAgent = agents.find(a => a.id === phoneCallForm.agent_id);
              const hasSecondary = selectedAgent?.elevenlabs_voice_id_secondary || selectedAgent?.voice_secondary;
              return hasSecondary ? (
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneCallForm.gender_switch}
                      onChange={(e) => setPhoneCallForm(prev => ({ ...prev, gender_switch: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                  <span className="text-sm text-foreground">{t('campaigns.genderSwitch') || 'Gender Switch'}</span>
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    {!webhookData?.valid_preview?.length
                      ? (t('campaigns.genderSwitchNeedsWebhook') || 'Load webhook data with contact name')
                      : (t('campaigns.genderSwitchHelp') || 'Swap voice based on contact name')
                    }
                  </span>
                </div>
              ) : null;
            })()}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={initiatingCall}
                className="btn-primary inline-flex items-center"
              >
                {initiatingCall ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('calls.initiating')}
                  </>
                ) : (
                  <>
                    <PhoneIcon className="h-5 w-5 mr-2" />
                    {t('calls.makePhoneCall')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Web Call Form */}
      {activeTab === 'web' && (
        <div className="card">
          <h3 className="text-lg font-medium text-foreground mb-4">
            <MicrophoneIcon className="h-5 w-5 inline mr-2" />
            {t('calls.initiateWebCall')}
          </h3>

          {webCallActive ? (
            <div className="text-center py-8">
              {/* Voice indicator circle */}
              <div className="relative mx-auto mb-4" style={{ width: '120px', height: '120px' }}>
                {/* Outer pulsing ring */}
                <div
                  className={`absolute inset-0 rounded-full ${
                    isAgentSpeaking
                      ? 'bg-green-400 animate-ping'
                      : 'bg-blue-400 animate-pulse'
                  }`}
                  style={{ opacity: 0.3 }}
                />
                {/* Inner solid circle */}
                <div
                  className={`absolute inset-3 rounded-full flex items-center justify-center ${
                    isAgentSpeaking
                      ? 'bg-green-500'
                      : 'bg-blue-500'
                  }`}
                  style={{
                    boxShadow: isAgentSpeaking
                      ? '0 0 20px rgba(34, 197, 94, 0.5)'
                      : '0 0 20px rgba(59, 130, 246, 0.5)'
                  }}
                >
                  <span className="text-white text-xs font-medium">
                    {isAgentSpeaking ? t('calls.speaking') : t('calls.listening')}
                  </span>
                </div>
              </div>
              <p className="text-lg font-medium text-foreground mb-2">
                {isAgentSpeaking ? t('calls.agentSpeaking') : t('calls.agentListening')}
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>{t('calls.session')}: {activeWebSession ? activeWebSession.substring(0, 8) : 'N/A'}...</p>
              {activeWebSessionDemo && (
                <div className="mx-auto mb-4 max-w-md border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 text-left" role="status" data-testid="web-demo-active-banner">
                  <span className="px-2 py-0.5 mr-2 rounded text-[10px] font-bold tracking-wide bg-amber-500 text-white">{t('calls.demo.badge')}</span>
                  <span className="text-xs text-amber-900">{t('calls.demo.activeBanner')}</span>
                </div>
              )}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEndWebCall('user_click'); }}
                className="btn-danger inline-flex items-center"
              >
                <StopIcon className="h-5 w-5 mr-2" />
                {t('calls.endCall')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleInitiateWebCall} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="web_agent_id" className="block text-sm font-medium text-foreground">
                    {t('calls.selectAgent')}
                  </label>
                  <select
                    id="web_agent_id"
                    value={webCallForm.agent_id}
                    onChange={(e) => {
                      setWebCallForm(prev => ({ ...prev, agent_id: e.target.value }));
                      fetchAgentTools(e.target.value);
                    }}
                    className="input-field mt-1"
                    required
                  >
                    <option value="">{t('calls.chooseAgent')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="mic_select" className="block text-sm font-medium text-foreground">
                    {t('calls.microphone')}
                  </label>
                  <select
                    id="mic_select"
                    value={selectedMicId}
                    onChange={(e) => setSelectedMicId(e.target.value)}
                    className="input-field mt-1"
                  >
                    <option value="">{t('calls.default')}</option>
                    {audioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Mic ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Audio Settings */}
              <div className="flex justify-start">
                <div className="inline-flex items-center space-x-3 px-4 py-3 bg-blue-50 rounded-full border border-blue-200">
                  <div className="p-2 bg-blue-500 rounded-full">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-blue-700">{t('calls.micGain')}</span>
                  <input
                    type="range"
                    id="input_gain"
                    min="1"
                    max="5"
                    step="0.5"
                    value={inputGain}
                    onChange={(e) => setInputGain(parseFloat(e.target.value))}
                    className="w-24 h-2 rounded-full cursor-pointer slider-gain"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((inputGain - 1) / 4) * 100}%, #dbeafe ${((inputGain - 1) / 4) * 100}%, #dbeafe 100%)`
                    }}
                  />
                  <style>{`
                    .slider-gain {
                      -webkit-appearance: none;
                      appearance: none;
                    }
                    .slider-gain::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      appearance: none;
                      width: 18px;
                      height: 18px;
                      background: white;
                      border: 2px solid #3b82f6;
                      border-radius: 50%;
                      cursor: pointer;
                      box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
                      transition: transform 0.15s ease, box-shadow 0.15s ease;
                    }
                    .slider-gain::-webkit-slider-thumb:hover {
                      transform: scale(1.1);
                      box-shadow: 0 3px 8px rgba(59, 130, 246, 0.4);
                    }
                    .slider-gain::-webkit-slider-thumb:active {
                      transform: scale(0.95);
                    }
                    .slider-gain::-moz-range-thumb {
                      width: 18px;
                      height: 18px;
                      background: white;
                      border: 2px solid #3b82f6;
                      border-radius: 50%;
                      cursor: pointer;
                      box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
                      transition: transform 0.15s ease, box-shadow 0.15s ease;
                    }
                    .slider-gain::-moz-range-thumb:hover {
                      transform: scale(1.1);
                    }
                  `}</style>
                  <span className="text-sm font-bold text-blue-600 bg-card px-2 py-1 rounded-full shadow-sm min-w-[2.5rem] text-center">
                    {inputGain}x
                  </span>
                </div>
              </div>

              {/* Use demo data (web only). OFF by default; ON needs no webhook. */}
              <div className="border border-border rounded-xl p-4 bg-background" data-testid="web-demo-section">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <label htmlFor="web_demo_toggle" id="web_demo_toggle_label" className="block text-sm font-medium text-foreground">
                      {t('calls.demo.toggleLabel')}
                    </label>
                    <p id="web_demo_toggle_help" className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                      {webCallForm.agent_id ? t('calls.demo.toggleHelp') : t('calls.demo.needsAgent')}
                    </p>
                  </div>
                  <button
                    type="button"
                    id="web_demo_toggle"
                    role="switch"
                    aria-checked={webDemoMode}
                    aria-labelledby="web_demo_toggle_label"
                    aria-describedby="web_demo_toggle_help"
                    disabled={!webCallForm.agent_id || initiatingCall}
                    onClick={() => applyWebDemoMode(!webDemoMode)}
                    className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      webDemoMode ? 'bg-amber-500 border-amber-600 text-white' : 'bg-card border-border text-foreground'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-block h-3 w-3 rounded-full ${webDemoMode ? 'bg-white' : 'bg-gray-400'}`}
                    />
                    {webDemoMode ? t('calls.demo.on') : t('calls.demo.off')}
                  </button>
                </div>

                {webDemoMode && (
                  <div className="mt-3" data-testid="web-demo-preview" aria-live="polite">
                    {demoFixtureStatus === 'loading' && (
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{t('calls.demo.loading')}</p>
                    )}
                    {demoFixtureStatus === 'error' && (
                      <p className="text-xs text-red-600" role="alert">{t('calls.demo.unavailableServer')}</p>
                    )}
                    {demoFixtureStatus === 'ready' && demoFixture && (
                      <div className="border border-amber-300 rounded-lg bg-amber-50 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wide bg-amber-500 text-white">
                            {t('calls.demo.badge')}
                          </span>
                          <span className="text-sm font-medium text-amber-900">{t('calls.demo.previewTitle')}</span>
                        </div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-amber-900">
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.customer')}:</dt><dd data-testid="demo-customer">{demoFixture.display.customer_name}</dd></div>
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.vehicle')}:</dt><dd data-testid="demo-vehicle">{demoFixture.display.model} / {demoFixture.display.year}</dd></div>
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.vinLast8')}:</dt><dd className="font-mono" data-testid="demo-vin8">{demoFixture.display.vin_last8}</dd></div>
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.lastService')}:</dt><dd data-testid="demo-last-service">{demoFixture.display.last_service_date}</dd></div>
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.historicalKm')}:</dt><dd data-testid="demo-hist-km">{demoFixture.display.historical_mileage_km} km</dd></div>
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.currentKm')}:</dt><dd>{t('calls.demo.unknown')}</dd></div>
                          <div className="flex gap-1"><dt className="font-medium">{t('calls.demo.price')}:</dt><dd>{t('calls.demo.unavailable')}</dd></div>
                        </dl>
                        <p className="text-[11px] mt-2 text-amber-800">{t('calls.demo.fixtureNote')}</p>
                        <p className="text-[11px] mt-1 font-medium text-amber-900">{t('calls.demo.actionsBlocked')}</p>
                        <p className="text-[11px] mt-1 text-amber-800">{t('calls.demo.nextSessionOnly')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Test Webhook URL — campaign-style locked/editable (hidden while demo data is ON) */}
              {!webDemoMode && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('calls.testWebhookUrl')}
                </label>

                {webhookData && webhookData.valid_count > 0 ? (
                  /* ── Locked state (data loaded) — campaign-style validation stats ── */
                  <div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={webCallForm.test_webhook_url || ''}
                        className="flex-1 px-3 py-2.5 bg-secondary border border-green-300 rounded-lg text-sm font-mono cursor-default"
                        style={{ color: 'var(--muted)' }}
                      />
                      <button
                        type="button"
                        onClick={() => { setWebhookData(null); setFieldDescriptions({}); setFieldFormats({}); }}
                        className="px-3 py-2.5 border border-border rounded-lg text-sm hover:bg-background transition-colors"
                        style={{ color: 'var(--muted)' }}
                        title={t('common.edit')}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Validation stat cards — matches CampaignWizard */}
                    <div className="mt-3 grid grid-cols-3 gap-3">
                      <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                        <p className="text-xl font-semibold text-green-700">{webhookData.valid_count || 0}</p>
                        <p className="text-xs text-green-600">{t('calls.validNumbers')}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                        <p className="text-xl font-semibold text-red-700">{webhookData.invalid_count || 0}</p>
                        <p className="text-xs text-red-600">{t('calls.invalidNumbers')}</p>
                      </div>
                      {/* Duplicados chip: surface rows that SHARE a phone (kept), not
                          just collapsed duplicates. See Gran Chapur shared-phone badge. */}
                      <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                        <p className="text-xl font-semibold text-yellow-700">
                          {(webhookData.shared_phone_count || 0) > 0
                            ? (webhookData.shared_phone_count || 0)
                            : (webhookData.duplicate_count || 0)}
                        </p>
                        <p className="text-xs text-yellow-600">
                          {(webhookData.shared_phone_count || 0) > 0 && (webhookData.duplicate_count || 0) > 0
                            ? t('calls.duplicatesShared', {
                                shared: webhookData.shared_phone_count || 0,
                                dropped: webhookData.duplicate_count || 0,
                              })
                            : (webhookData.shared_phone_count || 0) > 0
                              ? t('calls.sharedPhones')
                              : t('calls.duplicates')}
                        </p>
                      </div>
                    </div>

                    {/* Phone / Name / Context preview table — matches CampaignWizard */}
                    {webhookData.valid_preview?.length > 0 && (
                      <div className="mt-3 border border-green-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-green-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-green-800 text-xs">{t('calls.phone')}</th>
                              <th className="text-left px-3 py-2 font-medium text-green-800 text-xs">{t('calls.contactName')}</th>
                              <th className="text-left px-3 py-2 font-medium text-green-800 text-xs">{t('calls.context')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-green-100">
                            {webhookData.valid_preview.map((c, i) => (
                              <tr key={i} className={c.shared_phone ? 'bg-yellow-50' : 'hover:bg-green-50/50'}>
                                <td className="px-3 py-1.5 font-mono text-xs">
                                  {c.phone}
                                  {c.shared_phone && (
                                    <span
                                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-200 text-yellow-800 align-middle"
                                      title={t('calls.sharedPhoneRowHint')}
                                    >
                                      {t('calls.sharedPhoneBadge')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-xs">{c.name || '-'}</td>
                                <td className="px-3 py-1.5 text-xs" style={{ color: 'var(--muted)' }}>
                                  {c.context && Object.keys(c.context).length > 0
                                    ? Object.keys(c.context).join(', ')
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Data Dictionary — field descriptions for AI context */}
                    {webhookData.valid_preview?.length > 0 && (() => {
                      // Include top-level name field (extracted by validator from NOMBRE/nombre)
                      // so the user can set a format/description for it
                      const topLevelKeys = [];
                      if (webhookData.valid_preview.some(c => c.name)) topLevelKeys.push('name');
                      const contextKeys = [...new Set([
                        ...topLevelKeys,
                        ...webhookData.valid_preview.flatMap(c => c.context ? Object.keys(c.context) : []),
                      ])];
                      if (contextKeys.length === 0) return null;

                      const hasDescriptions = Object.values(fieldDescriptions).some(v => v && v.trim());

                      const handleLoadTemplate = (templateId) => {
                        if (!templateId) return;
                        const tpl = (fieldTemplates || []).find(t => t.template_id === templateId);
                        if (!tpl) return;
                        const merged = { ...fieldDescriptions };
                        Object.entries(tpl.field_descriptions || {}).forEach(([k, v]) => {
                          if (contextKeys.includes(k)) merged[k] = v;
                        });
                        setFieldDescriptions(merged);
                        // Also merge field_formats from template
                        const mergedFormats = { ...fieldFormats };
                        Object.entries(tpl.field_formats || {}).forEach(([k, v]) => {
                          if (contextKeys.includes(k)) mergedFormats[k] = v;
                        });
                        setFieldFormats(mergedFormats);
                        toast.success(t('campaignWizard.templateLoaded'));
                      };

                      const handleSaveTemplate = async () => {
                        const name = templateName.trim();
                        if (!name) return;
                        setSavingTemplate(true);
                        try {
                          const res = await createFieldTemplate(name, fieldDescriptions, fieldFormats);
                          setFieldTemplates(prev => [...prev, res.data]);
                          setShowSaveTemplate(false);
                          setTemplateName('');
                          toast.success(t('campaignWizard.templateSaved'));
                        } catch (err) {
                          const msg = err.response?.data?.detail || 'Failed to save template';
                          toast.error(msg);
                        } finally {
                          setSavingTemplate(false);
                        }
                      };

                      return (
                        <div className="mt-4">
                          <div className="mb-3">
                            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                              <DocumentTextIcon className="w-4 h-4" />
                              {t('campaignWizard.dataDictionary')}
                            </h4>
                            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                              {t('campaignWizard.dataDictionaryHelp')}
                            </p>
                          </div>

                          {/* Template Load / Save row */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="relative flex-1" ref={templateDropdownRef}>
                              <button
                                type="button"
                                onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                                className="w-full flex items-center justify-between px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-card"
                              >
                                <span style={{ color: 'var(--muted)' }}>{t('campaignWizard.loadTemplate')}</span>
                                <ChevronDownIcon className="w-3.5 h-3.5" style={{ color: 'var(--muted)' }} />
                              </button>
                              {templateDropdownOpen && (
                                <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded shadow-lg max-h-48 overflow-y-auto">
                                  {(fieldTemplates || []).length === 0 ? (
                                    <div className="px-3 py-2 text-xs" style={{ color: 'var(--muted)' }}>
                                      {t('campaignWizard.noTemplates')}
                                    </div>
                                  ) : (
                                    (fieldTemplates || []).map((tpl) => (
                                      <div
                                        key={tpl.template_id}
                                        className="flex items-center justify-between px-3 py-2 hover:bg-background transition-colors group"
                                      >
                                        <button
                                          type="button"
                                          className="flex-1 text-left text-xs text-foreground truncate"
                                          onClick={() => {
                                            handleLoadTemplate(tpl.template_id);
                                            setTemplateDropdownOpen(false);
                                          }}
                                        >
                                          {tpl.name}
                                          <span className="ml-1" style={{ color: 'var(--muted)' }}>
                                            ({Object.keys(tpl.field_descriptions || {}).length})
                                          </span>
                                        </button>
                                        <button
                                          type="button"
                                          className="ml-2 p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                          title={t('common.delete')}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!window.confirm(t('campaignWizard.deleteTemplateConfirm', { name: tpl.name }))) return;
                                            try {
                                              await deleteFieldTemplate(tpl.template_id);
                                              setFieldTemplates(prev => prev.filter(x => x.template_id !== tpl.template_id));
                                              toast.success(t('campaignWizard.templateDeleted'));
                                            } catch (err) {
                                              toast.error(err.response?.data?.detail || 'Failed to delete template');
                                            }
                                          }}
                                        >
                                          <TrashIcon className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>

                            {!showSaveTemplate ? (
                              <button
                                type="button"
                                onClick={() => setShowSaveTemplate(true)}
                                disabled={!hasDescriptions}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                <BookmarkIcon className="w-3.5 h-3.5" />
                                {t('campaignWizard.saveAsTemplate')}
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={templateName}
                                  onChange={(e) => setTemplateName(e.target.value)}
                                  placeholder={t('campaignWizard.templateNamePlaceholder')}
                                  maxLength={100}
                                  className="px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 w-40"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveTemplate();
                                    if (e.key === 'Escape') { setShowSaveTemplate(false); setTemplateName(''); }
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={handleSaveTemplate}
                                  disabled={!templateName.trim() || savingTemplate}
                                  className="px-3 py-1.5 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40"
                                >
                                  {savingTemplate ? '...' : t('common.save')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                                  className="p-1" style={{ color: 'var(--muted)' }}
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2 border border-border rounded-xl p-4 max-h-64 overflow-y-auto bg-card">
                            {contextKeys.map((key) => (
                              <div key={key} className="flex items-center gap-3">
                                <span className="text-xs font-mono w-36 flex-shrink-0 truncate" style={{ color: 'var(--muted)' }} title={key}>
                                  {key}
                                </span>
                                <input
                                  type="text"
                                  value={fieldDescriptions[key] || ''}
                                  onChange={(e) => {
                                    setFieldDescriptions(prev => ({ ...prev, [key]: e.target.value }));
                                  }}
                                  placeholder={t('campaignWizard.dataDictionaryPlaceholder')}
                                  maxLength={100}
                                  className="flex-1 px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-card"
                                />
                                <select
                                  value={fieldFormats[key] || 'none'}
                                  onChange={(e) => {
                                    setFieldFormats(prev => ({ ...prev, [key]: e.target.value }));
                                  }}
                                  className="w-32 flex-shrink-0 px-2 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-card"
                                  title={t('campaignWizard.fieldFormat', 'Format')}
                                >
                                  <option value="none">Auto</option>
                                  <option value="name_ap1_ap2_nom1_nom2">Nombre: AP1 AP2 NOM1 NOM2</option>
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  /* ── Editable state — preset dropdown + paste URL and fetch ── */
                  <div className="space-y-2">
                    {webhookPresets.length > 0 && (
                      <select
                        value=""
                        onChange={(e) => {
                          const preset = webhookPresets.find(p => p.id === e.target.value);
                          if (preset) {
                            setWebCallForm(prev => ({ ...prev, test_webhook_url: preset.url }));
                            handleLoadWebhook(preset.url);
                          }
                        }}
                        className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">{t('calls.selectWebhookPreset')}</option>
                        {webhookPresets.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    )}
                    <div className="flex gap-2">
                      <textarea
                        id="test_webhook_url"
                        rows={2}
                        value={webCallForm.test_webhook_url}
                        onChange={(e) => setWebCallForm(prev => ({ ...prev, test_webhook_url: e.target.value }))}
                        className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-card resize-y overflow-y-auto"
                        placeholder="https://structdatafy.example.com/api/webhook/..."
                      />
                      <button
                        type="button"
                        onClick={() => handleLoadWebhook()}
                        disabled={!webCallForm.test_webhook_url?.trim() || webhookLoading}
                        className={`inline-flex items-center px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                          webCallForm.test_webhook_url?.trim() && !webhookLoading
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-secondary cursor-not-allowed'
                        }`}
                      >
                        {webhookLoading ? (
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        ) : (
                          t('calls.loadWebhook')
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>{t('calls.testWebhookHelp')}</p>
                  </div>
                )}
              </div>
              )}

              {/* Agent Tools Preview */}
              {webCallForm.agent_id && (
                <div className="border border-border rounded-xl p-4 bg-background">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <WrenchScrewdriverIcon className="h-5 w-5 mr-2" style={{ color: 'var(--muted)' }} />
                      <h4 className="text-sm font-medium text-foreground">{t('calls.agentTools')}</h4>
                      <span className="ml-2 text-xs" style={{ color: 'var(--muted)' }}>({agentTools.length})</span>
                    </div>
                    {agentTools.some(t => t.webhook_url) && (
                      <button
                        type="button"
                        onClick={verifyAllTools}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
                        {t('calls.verifyWebhooks')}
                      </button>
                    )}
                  </div>
                  {toolsLoading ? (
                    <div className="flex justify-center py-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    </div>
                  ) : agentTools.length === 0 ? (
                    <p className="text-xs py-2" style={{ color: 'var(--muted)' }}>{t('calls.noToolsConfigured')}</p>
                  ) : (
                    <div className="space-y-2">
                      {agentTools.map(tool => (
                        <div key={tool.id} className="flex items-center justify-between bg-card rounded px-3 py-2 border border-border">
                          <div className="flex items-center min-w-0">
                            <span className="text-sm font-mono text-foreground truncate">{tool.name}</span>
                            {tool.webhook_url ? (
                              <span className="ml-2 flex items-center text-xs text-green-700">
                                <LinkIcon className="h-3 w-3 mr-0.5" />
                                <span className="truncate max-w-[200px]">{tool.webhook_url}</span>
                              </span>
                            ) : (
                              <span className="ml-2 text-xs" style={{ color: 'var(--muted)' }}>{t('calls.noWebhook')}</span>
                            )}
                          </div>
                          <div className="flex items-center ml-2">
                            {tool.webhook_url && (
                              <>
                                {toolsVerified[tool.id] === 'checking' && (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                )}
                                {toolsVerified[tool.id] === 'ok' && (
                                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                )}
                                {toolsVerified[tool.id] === 'error' && (
                                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                                )}
                                {!toolsVerified[tool.id] && (
                                  <button
                                    type="button"
                                    onClick={() => verifyToolWebhook(tool.id)}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    {t('calls.verify')}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Gender switch toggle — show when agent has secondary voice */}
              {(() => {
                const selectedAgent = agents.find(a => a.id === webCallForm.agent_id);
                const hasSecondary = selectedAgent?.elevenlabs_voice_id_secondary || selectedAgent?.voice_secondary;
                return hasSecondary ? (
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={webCallForm.gender_switch}
                        onChange={(e) => setWebCallForm(prev => ({ ...prev, gender_switch: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                    <span className="text-sm text-foreground">{t('campaigns.genderSwitch') || 'Gender Switch'}</span>
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>
                      {!webhookData?.valid_preview?.length
                        ? (t('campaigns.genderSwitchNeedsWebhook') || 'Load webhook data with contact name')
                        : (t('campaigns.genderSwitchHelp') || 'Swap voice based on contact name')
                      }
                    </span>
                  </div>
                ) : null;
              })()}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={initiatingCall || (webDemoMode && demoFixtureStatus !== 'ready')}
                  className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {initiatingCall ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('calls.connecting')}
                    </>
                  ) : (
                    <>
                      <MicrophoneIcon className="h-5 w-5 mr-2" />
                      {t('calls.startWebCall')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Call Sessions Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground">
            {activeTab === 'web' ? t('calls.webCalls') : t('calls.phoneCallSessions')}
          </h3>
          <button
            onClick={fetchData}
            className="text-sm text-primary-600 hover:text-primary-500"
          >
            {t('common.refresh')}
          </button>
        </div>

        {currentCalls.length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'web' ? (
              <GlobeAltIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
            ) : (
              <PhoneIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
            )}
            <h3 className="mt-2 text-sm font-medium text-foreground">{activeTab === 'web' ? t('calls.noWebCalls') : t('calls.noPhoneCalls')}</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('dashboard.getStartedCalls')}</p>
          </div>
        ) : (
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background" style={{ color: 'var(--muted)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    {t('calls.sessionHeader')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    {t('calls.agent')}
                  </th>
                  {activeTab === 'phone' && (
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      {t('calls.phoneNumber')}
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    {t('calls.started')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {[...currentCalls].sort((a, b) => new Date(b.started_at) - new Date(a.started_at)).map((call) => (
                  <tr key={call.session_id || call.call_sid || Math.random()} className="hover:bg-hover">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(call.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-foreground">
                            {(call.session_id || call.call_sid || 'N/A').substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 mr-2" style={{ color: 'var(--muted)' }} />
                        <div className="text-sm text-foreground">
                          {call.agent_name || getAgentName(call.agent_id)}
                        </div>
                      </div>
                    </td>
                    {activeTab === 'phone' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {call.phone_number || 'N/A'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-badge ${getStatusColor(call.status)}`}>
                        {t(callStatuses[call.status]?.labelKey || `callStatuses.${call.status}`, { defaultValue: call.status })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--muted)' }}>
                      {new Date(call.started_at).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewTranscript(call.call_sid || call.session_id || call.stream_sid)}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center"
                      >
                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                        {t('calls.viewTranscript')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Call Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PhoneIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium" style={{ color: 'var(--muted)' }}>{t('calls.phoneCalls')}</p>
              <p className="text-lg font-semibold text-foreground">{phoneCalls.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PlayIcon className="h-8 w-8 text-warning-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium" style={{ color: 'var(--muted)' }}>{t('calls.activeCalls')}</p>
              <p className="text-lg font-semibold text-foreground">
                {allCalls.filter(call => call.status === 'connected' || call.status === 'initiated').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8" style={{ color: 'var(--muted)' }} />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium" style={{ color: 'var(--muted)' }}>{t('calls.totalCalls')}</p>
              <p className="text-lg font-semibold text-foreground">{allCalls.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transcript Modal */}
      {transcriptModal.isOpen && (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-50" style={{ backgroundColor: 'var(--overlay)' }}>
          <div className="relative top-20 mx-auto p-5 border border-border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-xl bg-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                {t('calls.callTranscript')}
              </h3>
              <button
                onClick={closeTranscriptModal}
                style={{ color: 'var(--muted)' }}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {transcriptModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2" style={{ color: 'var(--muted)' }}>{t('calls.loadingTranscript')}</span>
              </div>
            ) : transcriptModal.transcript ? (
              <div className="space-y-4">
                <div className="bg-background p-4 rounded-xl">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-foreground">{t('calls.callId')}:</span>
                      <span className="ml-2 text-foreground">{transcriptModal.transcript.call_id}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">{t('calls.agent')}:</span>
                      <span className="ml-2 text-foreground">{transcriptModal.transcript.agent_name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">{t('calls.startTime')}:</span>
                      <span className="ml-2 text-foreground">
                        {transcriptModal.transcript.start_time ? new Date(transcriptModal.transcript.start_time).toLocaleString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
                        }) : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">{t('calls.endTime')}:</span>
                      <span className="ml-2 text-foreground">
                        {transcriptModal.transcript.end_time
                          ? new Date(transcriptModal.transcript.end_time).toLocaleString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
                            })
                          : t('calls.inProgress')
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {transcriptModal.transcript.summary && (
                  <div className="bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-300 mb-2">{t('calls.callSummary')}</h4>
                    <div className="text-sm text-indigo-900 dark:text-indigo-200 whitespace-pre-line">
                      {transcriptModal.transcript.summary}
                    </div>
                  </div>
                )}

                <div className="max-h-96 overflow-y-auto border border-border rounded-xl">
                  {transcriptModal.transcript.transcript && transcriptModal.transcript.transcript.length > 0 ? (
                    <div className="p-4 space-y-3">
                      {[...transcriptModal.transcript.transcript].sort((a, b) => {
                        if (!a.timestamp && !b.timestamp) return (a.seq ?? 0) - (b.seq ?? 0);
                        if (!a.timestamp) return 1;
                        if (!b.timestamp) return -1;
                        if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? -1 : 1;
                        if (a.seq != null && b.seq != null) return a.seq - b.seq;
                        return a.speaker === 'user' ? -1 : 1;
                      }).map((entry, index) => (
                        <div key={index} className={`p-3 rounded-xl ${
                          entry.speaker === 'user'
                            ? 'bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-400 dark:border-blue-500'
                            : 'bg-green-50 dark:bg-green-950/40 border-l-4 border-green-400 dark:border-green-500'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-medium ${
                              entry.speaker === 'user' ? 'text-blue-800 dark:text-blue-300' : 'text-green-800 dark:text-green-300'
                            }`}>
                              {entry.speaker === 'user' ? t('calls.customer') : (transcriptModal.transcript.agent_name || t('calls.agent'))}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>
                              {new Date(entry.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-foreground">{typeof entry.text === 'string' ? entry.text : ''}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center" style={{ color: 'var(--muted)' }}>
                      <DocumentTextIcon className="mx-auto h-12 w-12 mb-4" style={{ color: 'var(--muted)' }} />
                      <p>{t('calls.noTranscript')}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
                <p>{t('calls.failedLoadTranscript')}</p>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={closeTranscriptModal}
                className="px-4 py-2 bg-secondary text-foreground rounded-full hover:bg-hover"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Calls;
